<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span>Migrate Data: {{ cohort.title }}</span>
    </v-card-title>
    <v-divider />
    <v-card-text class="pa-6">
      <v-row v-if="error">
        <v-col>
          <v-alert density="compact" v-if="error" type="error" class="mb-4"> {{ error }}</v-alert>
        </v-col>
      </v-row>

      <v-alert
        density="comfortable"
        type="info"
        variant="tonal"
        class="mb-4"
        icon="fa fa-info-circle"
      >
        This will migrate all users and access codes from this cohort to another cohort in the same institution.
      </v-alert>

      <v-card
        variant="outlined"
        class="mb-4"
      >
        <v-card-text class="pa-4">
          <div class="d-flex align-center mb-3">
            <v-chip
              color="primary"
              variant="flat"
              size="small"
              class="mr-2"
            >
              1
            </v-chip>
            <span class="text-h6">Select Cohort</span>
          </div>

          <tenant-search-autocomplete
            v-model="selectedCohort"
            entity-type="cohort"
            :institution-ids="[cohort.institutionMeta._id]"
          />

          <!--          <cohort-search-dropdown-->
          <!--            v-model="selectedCohort"-->
          <!--            :tenant="cohort.tenant as Tenant"-->
          <!--            :institution="cohort.institution as Institution"-->
          <!--            variant="outlined"-->
          <!--            density="comfortable"-->
          <!--            :multiple="false"-->
          <!--          />-->

          <v-btn
            v-if="!dryRunResult"
            class="mt-4"
            color="primary"
            variant="tonal"
            prepend-icon="far fa-magnifying-glass"
            @click="migrateCohortData"
            :disabled="!selectedCohort || isSubmitting"
            :loading="isSubmitting"
            block
          >
            Test Impact
          </v-btn>
        </v-card-text>
      </v-card>

      <v-expand-transition>
        <v-card
          v-if="dryRunResult"
          color="error"
          variant="outlined"
          class="mb-4"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-icon color="error" class="mr-2">far fa-alert-circle</v-icon>
              <span class="text-h6 text-error">Danger Zone</span>
              <v-chip class="ml-2" color="error" variant="tonal" size="small">
                This action cannot be undone
              </v-chip>
            </div>

            <v-alert
              type="error"
              variant="tonal"
              density="comfortable"
              class="mb-4"
            >
              <div class="font-weight-medium mb-2">This migration will affect the following:</div>
              <v-row dense>
                <v-col cols="auto">
                  <v-chip color="error" variant="flat" size="small">
                    Users: {{ dryRunResult.data.result.usersCount }}
                  </v-chip>
                </v-col>
                <v-col cols="auto">
                  <v-chip color="error" variant="flat" size="small">
                    Access Codes: {{ dryRunResult.data.result.accessCodesCount }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-alert>

            <danger-confirmation-field
              v-model="confirmDanger"
              message=""
              confirm-string="CONFIRM"
              :action-label="$t('common.action_label')"
            />
          </v-card-text>
        </v-card>
      </v-expand-transition>
    </v-card-text>
    <v-divider />
    <v-card-actions class="pa-4">
      <v-spacer/>
      <v-btn
        variant="text"
        @click="$emit('close')"
      >
        {{ $t("buttons.cancel") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="far fa-check"
        @click="migrateCohortData(false)"
        v-bind:disabled="!confirmDanger || isSubmitting"
        :loading="isSubmitting"
      >
        {{ $t("buttons.confirm") }} Migrate
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import {PropType, ref, watch} from "vue";
import yapi from "@/lib/yapi";
import {Cohort, isYapiError, YapiError} from "@YenzaCT/sdk";
import {useGlobalStore} from "@/store";
import {useRoute} from "vue-router";
import DangerConfirmationField from "@/components/DangerConfirmationField.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const store = useGlobalStore();
const route = useRoute();

const confirmDanger = ref<boolean>(false);
const cohortId = route.params.cohortId as string;
const selectedCohort = ref<string>();
const dryRunResult = ref();
const error = ref<string | undefined>("");
const isSubmitting = ref<boolean>(false);

defineProps({
  cohort: {
    type: Object as PropType<Cohort>,
    required: true
  }
});

const emits = defineEmits<{
  (e: "close"): void;
  (e: "success"): void;
}
>();

watch(selectedCohort, () => {
  dryRunResult.value = undefined; // Reset dryRunResult when selectedCohort changes
});

const migrateCohortData = async (dryRun = true) => {
  try {
    isSubmitting.value = true;
    store.networkBusy = true;

    if (!selectedCohort.value || !selectedCohort.value) {
      error.value = "Please select a cohort before proceeding.";
      return;
    }

    if (dryRun) {
      dryRunResult.value = await yapi.admin.cohort.migrate(cohortId, selectedCohort.value);
      return;
    } else {
      await yapi.admin.cohort.migrate(cohortId, selectedCohort.value, false);
      emits("success");
      store.showSnackbar("Data has been migrated to " + selectedCohort.value);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    isSubmitting.value = false;
    store.networkBusy = false;
  }
};

</script>

<style scoped>
.v-card {
  overflow: visible;
}

.v-chip {
  font-weight: 500;
}

.text-h6 {
  font-size: 1.1rem !important;
}
</style>
